/**
 * Philippine rice yield forecast UX: Aug (year Y) → Jun (year Y + 1), including
 * trailing May/Jun for forward forecasts. Seasons roll based on today's date so
 * labels and farm API dates stay aligned with the backend.
 */

export const RICE_FORECAST_MONTH_ORDER = [
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun'
] as const;

export type RiceForecastMonthAbbr = (typeof RICE_FORECAST_MONTH_ORDER)[number];

const ABBR_TO_MM: Record<RiceForecastMonthAbbr, string> = {
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06'
};

/** Calendar year of the August that starts the current forecast season */
export function riceSeasonAugustYear(reference: Date = new Date()): number {
  const y = reference.getFullYear();
  const calendarMonth = reference.getMonth() + 1;
  return calendarMonth >= 8 ? y : y - 1;
}

/** Calendar year shown in labels (and used for MODIS month) */
export function forecastLabelCalendarYear(
  monthAbbr: RiceForecastMonthAbbr,
  seasonAugustYear: number
): number {
  const augThroughDec: RiceForecastMonthAbbr[] = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return augThroughDec.includes(monthAbbr) ? seasonAugustYear : seasonAugustYear + 1;
}

export interface LargeScaleRiceForecastMonthOption {
  value: RiceForecastMonthAbbr;
  label: string;
}

export function buildLargeScaleRiceForecastMonthOptions(
  reference: Date = new Date()
): LargeScaleRiceForecastMonthOption[] {
  const augYear = riceSeasonAugustYear(reference);
  return RICE_FORECAST_MONTH_ORDER.map((abbr) => ({
    value: abbr,
    label: `${abbr} ${forecastLabelCalendarYear(abbr, augYear)}`
  }));
}

export interface FarmRiceForecastMonthOption {
  /** Two-digit calendar month */
  value: string;
  label: string;
  year: number;
}

export function buildFarmRiceForecastMonthOptions(
  reference: Date = new Date()
): FarmRiceForecastMonthOption[] {
  const augYear = riceSeasonAugustYear(reference);
  return RICE_FORECAST_MONTH_ORDER.map((abbr) => {
    const y = forecastLabelCalendarYear(abbr, augYear);
    return {
      value: ABBR_TO_MM[abbr],
      label: `${abbr} ${y}`,
      year: y
    };
  });
}
